/**
 * Floor label printing picks a station (Inventory, Receiving, Shipping).
 * The hub resolves one printer with the label's stock size and DPI.
 */

import { useEffect, useMemo, useState } from "react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { useAuth } from "@/components/AuthProvider";
import { apiFetch } from "@/lib/api";
import {
  fetchZplPrinters,
  ZPL_PRINTERS_QUERY_KEY,
  type ZplPrinterListPayload,
  type ZplPrinterStorageKey,
} from "@/lib/zplPrinter";
import {
  isZplPrintStationId,
  resolveZplPrinterAtStation,
  stationIdFromStoredPrinter,
  type PublicStationOption,
} from "@/lib/zplPrintLocationMatch";
import {
  readFloorDeviceId,
  readScopedZplPrinter,
  writeScopedZplPrinter,
} from "@/lib/floorPrinterStorage";

export {
  isZplPrintStationId,
  resolveZplPrinterAtStation,
  stationIdFromStoredPrinter,
  zplPrintStationLabel,
  type PublicStationOption,
  type ZplLabelRequirement,
  type ZplPrinterResolution,
} from "@/lib/zplPrintLocationMatch";

export function useZplPrintLocation(options: {
  storageKey: ZplPrinterStorageKey | string;
  labelSize: string;
  dpi: number;
  fetchPrintersEnabled?: boolean;
}): {
  printersQuery: UseQueryResult<ZplPrinterListPayload>;
  stations: PublicStationOption[];
  station: string;
  setStation: (station: string) => void;
  selectedPrinter: string;
  unavailableMessage: string | null;
} {
  const { session } = useAuth();
  const userId = session?.user?.id ?? "";
  const deviceId = readFloorDeviceId();

  const printersQuery = useQuery({
    queryKey: ZPL_PRINTERS_QUERY_KEY,
    queryFn: fetchZplPrinters,
    staleTime: 120_000,
    enabled: options.fetchPrintersEnabled !== false,
  });
  const stationsQuery = useQuery({
    queryKey: ["public-stations"],
    queryFn: () => apiFetch<{ stations: PublicStationOption[] }>("/api/zpl-printers/stations"),
    staleTime: 120_000,
    enabled: options.fetchPrintersEnabled !== false,
  });

  const [station, setStationState] = useState("");

  useEffect(() => {
    const stored = readScopedZplPrinter(options.storageKey, {
      userId,
      deviceId,
      mobility: "mobile",
      fallback: "",
    });
    setStationState(
      stationIdFromStoredPrinter(stored, stationsQuery.data?.stations ?? [], printersQuery.data?.printers),
    );
  }, [deviceId, options.storageKey, printersQuery.data?.printers, stationsQuery.data?.stations, userId]);

  useEffect(() => {
    if (!isZplPrintStationId(station)) return;
    writeScopedZplPrinter(options.storageKey, station, { userId, deviceId });
  }, [deviceId, options.storageKey, station, userId]);

  const resolution = useMemo(
    () =>
      resolveZplPrinterAtStation(printersQuery.data?.printers ?? [], station, {
        labelSize: options.labelSize,
        dpi: options.dpi,
      }),
    [options.dpi, options.labelSize, printersQuery.data?.printers, station],
  );

  return {
    printersQuery,
    stations: stationsQuery.data?.stations ?? [],
    station,
    setStation: setStationState,
    selectedPrinter: resolution.printerName,
    unavailableMessage: station.trim() === "" ? null : resolution.unavailableMessage,
  };
}
